import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { NotifierService } from 'angular-notifier';

import { AccountsService } from './accounts.service';
import { AppUserClaimsService } from './app-user-claims.service';
import { AdminService } from './admin.service';
import { MiVBAccountSettingsService } from './mivb-account-settings.service';
import { TenantsService } from './tenants.service';

import WorkerCollection, { Odata, Utils, WorkflowService } from '@mitel/cloudlink-sdk';
import { alertUpdateTypes, asyncJobDataType, asyncJobOwner, asyncJobHttpMethod, asyncJobStatus, WorkerState } from '../constants/constants';
import { AsyncJobData, MiVBAsyncJobData, MiccbAsyncJobData, PolledJobResult, UserTemplateAsyncJobData } from '../interfaces/async-job-data';
import { EmployeeService } from '@mitel-internal/miccb-components';
import { AlertData } from '@mitel/cloudlink-console-components/lib/cl-alert/cl-alertdata';
import { Router } from '@angular/router';
import { Util } from '../utils/Util';
import { environment } from '../../../environments/environment';
import LRUCache from 'lru-cache';
import { BillingAsyncJobItem, BillingAsyncJobType, TenantType } from '../interfaces/tenants';


const logContext = 'AsyncJobsService';

@Injectable({
  providedIn: 'root'
})
export class AsyncJobsService {
  private _asyncJobs: AsyncJobData[] = [];
  public asyncJobsChanged = new Subject<AsyncJobData[]>();
  private _isMiVBRunning: boolean = false;
  private _isImportUserTemplatesWorkerRunning: boolean = false;
  private _isMiCCBUpdating: boolean = false;
  private readonly importUserTemplatesActivityId = environment.importUserTemplatesActivityId;
  //temporary list to store users that thrown exception when asking to admin API
  //reduce unnecessary calls to admin
  private _userListError: string[] = [];
  /**
   * cache user-name by user-id
   */
  private userNameCache = new LRUCache<string, Promise<string>>({ max: 1000 });

  constructor(
    private adminService: AdminService,
    private accountsService: AccountsService,
    private appUserClaimsService: AppUserClaimsService,
    private miVBAccountSettingsService: MiVBAccountSettingsService,
    private tenantsService: TenantsService,
    private notifierService: NotifierService,
    private translateService: TranslateService,
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    private router: Router) {
  }

  async initAsyncJobs(...owners: asyncJobOwner[]) {
    if (owners.find(owner => owner == asyncJobOwner.MIVB)) {
      if(!this._isMiVBRunning){
        console.log('MiVB async jobs service starts polling at: ', Date());
        this._isMiVBRunning = true;
        while(this._isMiVBRunning) {
          await this.getMiVBAsyncJobs();
          await Util.timeout(2000); // wait before polling again
        }
      }
    }

    if (owners.find(owner => owner == asyncJobOwner.USER_TEMPLATES)) {
      if(!this._isImportUserTemplatesWorkerRunning){
        console.log('User Templates async jobs service starts polling at: ', Date());
        this._isImportUserTemplatesWorkerRunning = true;
        while(this._isImportUserTemplatesWorkerRunning) {
          await this.getImportUserTemplateWorkers();
          await Util.timeout(2000); // wait before polling again
        }
      }
    }

    if (owners.find(owner => owner == asyncJobOwner.MICCB)) {
      if(!this._isMiCCBUpdating){
        console.log('MiCCB user async jobs service starts polling at: ', Date());
        this._isMiCCBUpdating = true;
        while(this._isMiCCBUpdating) {
          await this.getMiCCBAsyncJobs();
          await Util.timeout(2000);
        }
      }
    }
    
    const asyncJobTenantTypes = owners.filter(owner => owner == asyncJobOwner.ZAPPA_PHONE || owner == asyncJobOwner.UNIFY_PHONE);
    if (asyncJobTenantTypes?.length) {
      // no polling - should be notification
      await this.getTenantAsyncJobs(asyncJobTenantTypes);
    }
  }

  stopAsyncJobs(owner: asyncJobOwner){
    if(owner === asyncJobOwner.MIVB){
      if(this._isMiVBRunning){
        console.log('MiVB async jobs service stops polling at: ', Date());
        this._isMiVBRunning = false;
      }
    }
    if(owner === asyncJobOwner.USER_TEMPLATES){
      if(this._isImportUserTemplatesWorkerRunning){
        console.log('User Templates async jobs service stops polling at: ', Date());
        this._isImportUserTemplatesWorkerRunning = false;
      }
    }
    if(owner === asyncJobOwner.MICCB) {
      if(this._isMiCCBUpdating) {
        console.log('MiCCB user async job service stops polling at: ', Date());
        this._isMiCCBUpdating = false;
      }
    }
  }

  get asyncJobs(): AsyncJobData[] {
    return this._asyncJobs;
  }

  set asyncJobs(value: AsyncJobData[]) {
    this._asyncJobs = value ?? [];
    this.asyncJobsChanged.next([...this._asyncJobs]);
  }

  clearAsyncJobs(): void {
    this.asyncJobs = [];
  }

  removeAsyncJob(asyncJobId: string): void {
    let indexOf = this.asyncJobs.findIndex(aj => aj.asyncJobId === asyncJobId);
    if(indexOf !== -1){
      this.asyncJobs.splice(indexOf, 1);
    }
  }

  isAnyAsyncJobInProgress(owner?: asyncJobOwner): boolean {
    if (owner) {
      return !!this.asyncJobs?.find(asyncJob => asyncJob.owner == owner && asyncJob.status !== asyncJobStatus.COMPLETE && asyncJob.status !== asyncJobStatus.FAILED);
    }
    return !!this.asyncJobs?.find(asyncJob => asyncJob.status !== asyncJobStatus.COMPLETE && asyncJob.status !== asyncJobStatus.FAILED);
  }

  private getAccountId(): string {
    return this.accountsService.getCurrentAccount()?.accountId ?? this.appUserClaimsService.getClaims()?.accountId;
  }

  private getAsyncJobAlertString(status: asyncJobStatus, requestType?: asyncJobHttpMethod): string {
    let alertString = "async_jobs";
    let typeString = requestType && requestType === asyncJobHttpMethod.DELETE ? "users_delete" : "users_edit"; //for now just assume edit if not delete
    let statusString = "";
    switch (status) {
      case asyncJobStatus.COMPLETE:
        statusString = "successful_alert";
        break;
      case asyncJobStatus.FAILED:
        statusString = "failed_alert";
        break;
      default:
        statusString = "in_progress_alert";
        break;
    };
    return alertString + "."+typeString+"."+statusString; //e.g "async_jobs.users_delete.successful_alert"
  }

  private getAsyncJobToastString(status: asyncJobStatus, requestType?: asyncJobHttpMethod): string {
    let toastString = "async_jobs";
    let typeString = requestType && requestType === asyncJobHttpMethod.DELETE ? "users_delete" : "users_edit"; //for now just assume edit if not delete
    let statusString = "";
    switch (status) {
      case asyncJobStatus.COMPLETE:
        statusString = "successful_toast";
        break;
      case asyncJobStatus.FAILED:
        statusString = "failed_toast";
        break;
      default:
        statusString = "in_progress_toast";
        break;
    };
    return toastString + "."+typeString+"."+statusString; //e.g "async_jobs.users_delete.successful_toast"
  }

  async getMiCCBAsyncJobs() {
    const jobs: AsyncJobData [] = this.asyncJobs.filter(j => j.owner === asyncJobOwner.MICCB);
    if (jobs.length > 0 ) {
      const asyncJobData = jobs[0] as MiccbAsyncJobData;
      const userId = asyncJobData.asyncJobId;
      asyncJobData.userName = await this.getUserName(userId);

      await this.employeeService.getEmployeeByCloudlinkId(this.getAccountId(), userId);
      const employee = this.employeeService.employee;

      if(asyncJobData.httpMethod === asyncJobHttpMethod.POST || asyncJobData.httpMethod === asyncJobHttpMethod.PUT) {
        const lastModifiedOn = employee?.lastModified;
        const asyncStartedOn = asyncJobData.createdOn.toISOString();
        const modified = (Date.parse(lastModifiedOn) - Date.parse(asyncStartedOn)) > 0 ? true : false;
        if ( modified ){
          // employee modified success 
          asyncJobData.status = asyncJobStatus.COMPLETE;
          console.log(`[${logContext}.getMiCCBAsyncJobs] ${asyncJobData.httpMethod} success`, asyncJobData);
        } else {
          const asyncWaitInSec = (Date.now() - Date.parse(asyncStartedOn))/1000;
          // failed if took more than 30 sec
          if (asyncWaitInSec > 30 ){
            asyncJobData.status = asyncJobStatus.FAILED;
            console.error(`[${logContext}.getMiCCBAsyncJobs] ${asyncJobData.httpMethod} failed`, asyncJobData);
          }
        }
      }
     
      if (asyncJobData.status === asyncJobStatus.COMPLETE) {
        asyncJobData.messageData = { translationString: this.getAsyncJobAlertString(asyncJobStatus.COMPLETE, asyncJobData.httpMethod) };
        asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);
        asyncJobData.type = asyncJobDataType.SUCCESS;
        asyncJobData.dismissible = true;
        asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');

        const jobs = this.asyncJobs.filter(j => j.owner !== asyncJobOwner.MICCB);
        let newAsyncJobs = [asyncJobData];
        this.asyncJobs = jobs.concat(newAsyncJobs);

        this.notifierService.notify('success', this.translateService.instant(this.getAsyncJobToastString(asyncJobStatus.COMPLETE, asyncJobData.httpMethod), { user: asyncJobData.userName }));
        this.stopAsyncJobs(asyncJobData.owner);
      }

      if (asyncJobData.status === asyncJobStatus.FAILED) {
        asyncJobData.messageData = { translationString: this.getAsyncJobAlertString(asyncJobStatus.FAILED, asyncJobData.httpMethod) };
        asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);
        asyncJobData.type = asyncJobDataType.DANGER;
        asyncJobData.dismissible = true;
        asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');

        const jobs = this.asyncJobs.filter(j => j.owner !== asyncJobOwner.MICCB);
        let newAsyncJobs = [asyncJobData];
        this.asyncJobs = jobs.concat(newAsyncJobs);

        this.notifierService.notify('error', this.translateService.instant(this.getAsyncJobToastString(asyncJobStatus.FAILED, asyncJobData.httpMethod), { user: asyncJobData.userName }));
        this.stopAsyncJobs(asyncJobData.owner);
      }
      
    }
  }

  async getMiVBAsyncJobs() {
    try {
      let asyncJobs = await this.miVBAccountSettingsService.getAllAsyncJobs();
      
      if (asyncJobs && asyncJobs.length) {
        let accountId = this.getAccountId();
        let newAsyncJobs = []; 
        let update = false;
        asyncJobs = asyncJobs.filter(asyncJob => !asyncJob.acknowledged);
        for(let i = 0; i < asyncJobs.length; i++){
          let asyncJob = asyncJobs[i];
          let asyncJobData = this.asyncJobs?.find(aj => aj.asyncJobId === asyncJob.asyncJobId && aj.owner === asyncJobOwner.MIVB) as MiVBAsyncJobData;
          if (!asyncJobData) {
            asyncJobData = {
              asyncJobId: asyncJob.asyncJobId,
              userId: asyncJob.userId,
              httpMethod: asyncJob.httpMethod || asyncJobHttpMethod.PUT, //assuming put for now since httpMethod is a new attribute
              status: asyncJob.status,
              owner: asyncJobOwner.MIVB,
              createdOn: asyncJob.createdOn,
              modifiedOn: asyncJob.modifiedOn,
              details: this.getLocaleDateString(asyncJob.createdOn, this.translateService.currentLang),
              btnCloseText: this.translateService.instant('async_jobs.btn_close_msg'),
              link: `/accounts/${accountId}/users/${asyncJob.userId}/product-mivb?asyncJobId=${asyncJob.asyncJobId}`
            } as MiVBAsyncJobData;
  
            try {
              if(this._userListError.find(userId => userId === asyncJob.userId)){
                //fetching userId previously throw exception, not needed to ask again for the same user, continue
                continue;
              } else {
                asyncJobData.userName = await this.getUserName(asyncJob.userId);
              }
            }
            catch(error){
              //user doesn't belong to the account
              //add the userId to temp list, not needed to ask again for the same user
              this._userListError.push(asyncJob.userId);
              continue;
            }
  
            if (asyncJob.status === asyncJobStatus.COMPLETE) {
              asyncJobData.messageData = { translationString: this.getAsyncJobAlertString(asyncJobStatus.COMPLETE, asyncJobData.httpMethod) };
              asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);
              asyncJobData.linkMessage = asyncJobData.userName ?? asyncJob.userId;
              asyncJobData.type = asyncJobDataType.SUCCESS;
              asyncJobData.dismissible = true;
              asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');
            } else if (asyncJob.status === asyncJobStatus.FAILED) {
              asyncJobData.messageData = { translationString: this.getAsyncJobAlertString(asyncJobStatus.FAILED, asyncJobData.httpMethod) };
              asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);
              asyncJobData.linkMessage = asyncJobData.userName ?? asyncJob.userId;
              asyncJobData.type = asyncJobDataType.DANGER;
              asyncJobData.dismissible = true;
              asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');
            } else {
              asyncJobData.messageData = {
                translationString: this.getAsyncJobAlertString(asyncJobStatus.IN_PROGRESS, asyncJobData.httpMethod),
                translationStringData: { user: asyncJobData.userName ?? asyncJob.userId }
              };
              asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString, asyncJobData.messageData.translationStringData);
              asyncJobData.type = asyncJobDataType.IN_PROGRESS;
              asyncJobData.dismissible = false;
            }
            newAsyncJobs.push(asyncJobData);
          } else {
            if(!asyncJobData.createdOn) {
              asyncJobData.createdOn = asyncJob.createdOn;
              asyncJobData.details = this.getLocaleDateString(asyncJob.createdOn, this.translateService.currentLang);
              update = true; 
            }
            asyncJobData.modifiedOn = asyncJob.modifiedOn;
            
            if (asyncJobData.type == asyncJobDataType.IN_PROGRESS) {
              if(asyncJob.status === asyncJobStatus.COMPLETE || asyncJob.status === asyncJobStatus.FAILED){
                asyncJobData.status = asyncJob.status;
                asyncJobData.httpMethod = asyncJob.httpMethod || asyncJobHttpMethod.PUT;
                asyncJobData.linkMessage = asyncJobData.userName ?? asyncJob.userId;
                asyncJobData.dismissible = true;
                asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');
                asyncJobData.type = asyncJob.status === asyncJobStatus.COMPLETE ? asyncJobDataType.SUCCESS : asyncJobDataType.DANGER;
                asyncJobData.messageData = asyncJob.status === asyncJobStatus.COMPLETE ?
                  { translationString: this.getAsyncJobAlertString(asyncJobStatus.COMPLETE, asyncJobData.httpMethod) }: //COMPLETE
                  { translationString: this.getAsyncJobAlertString(asyncJobStatus.FAILED, asyncJobData.httpMethod) }; //FAILED
                asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);           
                update = true;
                if (asyncJob.status === asyncJobStatus.COMPLETE) {
                  console.log(`[${logContext}.getMiVBAsyncJobs] ${asyncJob.httpMethod} success`, asyncJob);
                  this.notifierService.notify('success', this.translateService.instant( this.getAsyncJobToastString(asyncJobStatus.COMPLETE, asyncJobData.httpMethod), { user: asyncJobData.linkMessage }));
                } else {
                  //handling two types of errors thrown by the mivb micro
                  //1 - event.reason.error.body?.error?.msg -> body: error: code, msg
                  //2 - event.reason.error.body?.message -> body: message
                  // let correlationId = event.reason.error.headers ? event.reason.error.headers['x-mitel-correlation-id'] : '';

                  let msg = asyncJob.results?.body?.error?.msg ?? asyncJob.results?.body?.message;
                  console.error(`[${logContext}.getMiVBAsyncJobs] ${asyncJob.httpMethod} fail ${msg}`, asyncJob);
                  this.notifierService.notify('error', this.translateService.instant(this.getAsyncJobToastString(asyncJobStatus.FAILED, asyncJobData.httpMethod), { user: asyncJobData.linkMessage }));
                }
              }
            }
          }
        }

        //don't re-add the job in case it was added by 'addAsyncJob'
        //double checking again, in case 'addAsyncJob' was called in between
        if(newAsyncJobs.length){
          newAsyncJobs = newAsyncJobs.filter(naj => !this.asyncJobs?.find(aj => aj.asyncJobId === naj.asyncJobId)) ?? [];
        }

        if(newAsyncJobs.length || update){
          this.asyncJobs = this.asyncJobs.concat(newAsyncJobs);
        }

        if(!this.isAnyAsyncJobInProgress(asyncJobOwner.MIVB)){
          //not need to continue polling as there's no more updates to come... for now
          this.stopAsyncJobs(asyncJobOwner.MIVB);
        }
      } else {
        this.stopAsyncJobs(asyncJobOwner.MIVB);
      }
    }
    catch (error) {
      console.log(`Unable to fetch async jobs `, error);
      this.stopAsyncJobs(asyncJobOwner.MIVB);
    }
  }

  private async getAllWorkersByActivityId(params?: Odata): Promise<WorkerCollection.Worker[]> {    
    return new Promise<WorkerCollection.Worker[]>(async (resolve, reject) => {
      let workers: WorkerCollection.Worker[] = [];
      try {
        const workerCollection = await this.workflowService.getWorkersByActivityId({ activityId: this.importUserTemplatesActivityId, ...params });
        if (workerCollection?._embedded?.items) {
          workers = workerCollection._embedded.items;
          if (workerCollection._links?.next) {
            const moreWorkers = await this.getAllWorkersByActivityId(Utils.getOdataNext(workerCollection));
            if (moreWorkers) {
              workers = workers.concat(moreWorkers);
            }
          }
        }
        resolve(workers);
      } catch (err) {
        reject(err);
      }
    });
  }

  async getImportUserTemplateWorkers() {
    try {
      // get an array of the "Import User Templates" workers
      let workers = await this.getAllWorkersByActivityId()
      
      if (workers && workers.length) {
        let newAsyncJobs = [];
        let update = false;

        for(let i = 0; i < workers.length; i++){
          let worker = workers[i];

          /**
           * Check if there is an existing asyncJob for the worker
           * if there is not: create one,
           * if there is: check the worker state
          */
          let asyncJobData = this.asyncJobs?.find(aj => aj.asyncJobId === worker.workerId && aj.owner === asyncJobOwner.USER_TEMPLATES) as UserTemplateAsyncJobData;
          if (!asyncJobData) {
            let templateName = worker.inputs?.find(input => input.id == "templateName" && input.value !== null)?.value  || undefined;
            let asyncJobData = {
              asyncJobId: worker.workerId,
              workerId: worker.workerId,
              activityId: this.importUserTemplatesActivityId,
              status: asyncJobStatus.IN_PROGRESS,
              type: asyncJobDataType.IN_PROGRESS,
              messageData: templateName ?
              {
                translationString: 'async_jobs.user_templates.byNameInProgress',
                translationStringData: { templateName: templateName },
              }:
              {
                translationString: 'async_jobs.user_templates.inProgress',
              },
              owner: asyncJobOwner.USER_TEMPLATES,
              dismissible: false,
              templateName: templateName,
              createdOn: worker.createdOn,
              modifiedOn: worker.lastModifiedOn,
              details: this.getLocaleDateString(worker.createdOn, this.translateService.currentLang),
              btnCloseText: this.translateService.instant('async_jobs.btn_close_msg'),
              link: `/accounts/${this.getAccountId()}/user-templates/${worker.workerId}`,
            } as UserTemplateAsyncJobData;
            asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString, asyncJobData.messageData.translationStringData);

            // We set the new asyncJobData to have 'in progress' status/type/message properties;
            // If the worker is not running or bookmarked, we need to update those properties appropriately
            if (worker && worker.state && worker.state != WorkerState.RUNNING && worker.state != WorkerState.BOOKMARKED) {
              this.getAsyncJobDataFromResponse(worker, asyncJobData, false);
            }
            newAsyncJobs.push(asyncJobData);

          } else if (asyncJobData.type === asyncJobDataType.IN_PROGRESS) {
            // Check the state of the worker:
            if(worker && worker.state) {
              // If the worker is stopped, we're done!
              if (worker.state === WorkerState.STOPPED) {
                update = true;
                this.getAsyncJobDataFromResponse(worker, asyncJobData, true);
              }
              // If the worker is not stopped, not running, and not bookmarked, stop - this is an unexpected state
              else if (worker.state !== WorkerState.RUNNING && worker.state !== WorkerState.BOOKMARKED) {
                update = true;
                this.getAsyncJobDataFromResponse(undefined, asyncJobData, true);
              }
            } else {
              // If the worker does not have a state, stop - we cannot deduce anything without the state
              update = true;
              this.getAsyncJobDataFromResponse(undefined, asyncJobData, true);
            }
          }
        }

        //don't re-add the job in case it was added by 'addAsyncJob'
        //double checking again, in case 'addAsyncJob' was called in between
        if(newAsyncJobs.length){
          newAsyncJobs = newAsyncJobs.filter(naj => !this.asyncJobs?.find(aj => aj.asyncJobId === naj.asyncJobId)) ?? [];
        }

        if(newAsyncJobs.length || update){
          this.asyncJobs = this.asyncJobs.concat(newAsyncJobs);
        }

        if(!this.isAnyAsyncJobInProgress(asyncJobOwner.USER_TEMPLATES)){
          // no need to continue polling as there's no more updates to come... for now
          this.stopAsyncJobs(asyncJobOwner.USER_TEMPLATES);
        }
      } else {
        this.stopAsyncJobs(asyncJobOwner.USER_TEMPLATES);
      }
    } catch(error) {
      console.log(`Unable to fetch workers for activityId: ${this.importUserTemplatesActivityId} -> ${error}`);
      this.stopAsyncJobs(asyncJobOwner.USER_TEMPLATES);
    }
  }

  // TENANT ASYNC JOB HANDLING
  //   zappa/unify phone async jobs come from the billing micro
  // they consist of two types for now: Integration deletion and report generation ( only for zoom )
  // inital constuction of the alert expected to change once we know more

  // ATM the alert for Integration deletion will look like:
  // integration delete in progress
  // "Zappa Phone integration delete in progress. (Date & Time)" or
  // "Unify Phone integration delete in progress. (Date & Time)"
  // integration delete failed
  // "Zappa Phone integration delete failed. (Date & Time)" or <-- TBD link? reason?
  // "Unify Phone integration delete failed"
  // integration delete succeeded
  // "Zappa Phone integration delete complete (Date & Time)" or <-- not seen often since if the integration does not exist, its alerts will not
  // "Unify Phone integration delete complete"

  // ATM the alert for Report generation will look like: 
  // Report generation in progress
  // "Zappa Phone report generation in progress. (Date & Time)"
  // Report generation failed
  // "Zappa Phone report generation failed. (Date & Time)" <-- TBD link? reason?
  // Report generation done
  // "Zappa Phone report generation complete. (Date & Time)"   <-- does the user need next steps?
  async getTenantAsyncJobs(asyncJobTenantTypes: asyncJobOwner[]) {
    let jobs = (await this.tenantsService.getAsyncJobs())?.filter(job => !job.acknowledged);
    let newAsyncJobs = [];
    let update = false;

    for (let i = 0; i < jobs.length; i++) {
      let asyncJob = jobs[i];
      let asyncJobData = this.asyncJobs?.find(aj => aj.asyncJobId === asyncJob.jobId && 
        asyncJobTenantTypes.find(type => type == aj.owner)) as AsyncJobData;
      if (!asyncJobData) {
        asyncJobData = this.createNewTenantAsyncJobData(asyncJob);
        newAsyncJobs.push(asyncJobData);
      } else {
        // this handles an async job update. Since this type of job will use notification I doubt this will apply?
        // remove if we find it is not used
        update = this.updateTenantAsyncJobData(asyncJobData, asyncJob);
      }
    }

    //don't re-add the job in case it was added by 'addAsyncJob'
    //double checking again, in case 'addAsyncJob' was called in between
    if (newAsyncJobs.length) {
      newAsyncJobs = newAsyncJobs.filter(naj => !this.asyncJobs?.find(aj => aj?.asyncJobId === naj?.asyncJobId)) ?? [];
    }

    if (newAsyncJobs.length || update) {
      this.asyncJobs = this.asyncJobs.concat(newAsyncJobs);
    }
  }

  createNewTenantAsyncJobData(asyncJob: BillingAsyncJobItem): AsyncJobData
  {

    let asyncJobData = {
      asyncJobId: asyncJob.jobId,
      status: asyncJob.status == asyncJobStatus.QUEUED ? asyncJobStatus.IN_PROGRESS : asyncJob.status,
      owner: this.determineTenantOwner(asyncJob.jobType, asyncJob.clientJobTag),
      createdOn: asyncJob.createdOn,
      modifiedOn: asyncJob.modifiedOn,
      details: this.getLocaleDateString(asyncJob.createdOn, this.translateService.currentLang),
      btnCloseText: this.translateService.instant('async_jobs.btn_close_msg'),
      // link: `/accounts/${accountId}/users/${asyncJob.userId}/product-mivb?asyncJobId=${asyncJob.asyncJobId}` <-- do we need a link?
    } as AsyncJobData;

    if (asyncJob.status === asyncJobStatus.COMPLETE) {
      asyncJobData.messageData = { translationString: this.getTenantAsyncJobAlertString(asyncJobStatus.COMPLETE, asyncJob.jobType, asyncJob.clientJobTag) };
      asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);
      //asyncJobData.linkMessage = What will we link to?
      asyncJobData.type = asyncJobDataType.SUCCESS;
      asyncJobData.dismissible = true;
      asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');
    } else if (asyncJob.status === asyncJobStatus.FAILED) {
      asyncJobData.messageData = { translationString: this.getTenantAsyncJobAlertString(asyncJobStatus.FAILED, asyncJob.jobType, asyncJob.clientJobTag) };
      asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);
      //asyncJobData.linkMessage = What will we link to?
      asyncJobData.type = asyncJobDataType.DANGER;
      asyncJobData.dismissible = true;
      asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');
      // For now, only need to show link for the failed report generation notifications
      if (asyncJob.jobType == BillingAsyncJobType.REPORT_REGENERATION) {
        asyncJobData.link = `/accounts/${this.getAccountId()}/zappa-support?tab=report&jobId=${asyncJob.jobId}`;
        asyncJobData.linkMessage = this.translateService.instant('async_jobs.zappa_phone_report.details');
      }
    } else {
      asyncJobData.messageData = {
        translationString: this.getTenantAsyncJobAlertString(asyncJobStatus.IN_PROGRESS, asyncJob.jobType, asyncJob.clientJobTag),
      };
      asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString, asyncJobData.messageData.translationStringData);
      asyncJobData.type = asyncJobDataType.IN_PROGRESS;
      asyncJobData.dismissible = false;
    }
    return asyncJobData;
  }

  determineTenantOwner(jobType: BillingAsyncJobType, clientJobTag?: any) {
    let owner = '';
    switch (jobType) {
      case BillingAsyncJobType.REPORT_REGENERATION:
        owner = asyncJobOwner.ZAPPA_PHONE;
        break;
      case BillingAsyncJobType.TENANT_DELETION:
        // backward compatibility when there is no clientJobTag it's a Zoom deletion
        owner = clientJobTag?.tenantType == TenantType.UNIFY_PHONE ? asyncJobOwner.UNIFY_PHONE : asyncJobOwner.ZAPPA_PHONE;
        break;
      default:
        break;
    };
    return owner;
  }

  updateTenantAsyncJobData(asyncJobData: AsyncJobData, asyncJob: BillingAsyncJobItem): boolean
  {
    let update = false;

    if(!asyncJobData.createdOn) {
      asyncJobData.createdOn = asyncJob.createdOn;
      asyncJobData.details = this.getLocaleDateString(asyncJob.createdOn, this.translateService.currentLang);
      update = true; 
    }
    asyncJobData.modifiedOn = asyncJob.modifiedOn;
    
    if (asyncJobData.status == asyncJobStatus.IN_PROGRESS) {
      if(asyncJob.status === asyncJobStatus.COMPLETE || asyncJob.status === asyncJobStatus.FAILED){
        asyncJobData.status = asyncJob.status;
        //asyncJobData.linkMessage = will we have a link message
        asyncJobData.dismissible = true;
        asyncJobData.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg');
        asyncJobData.type = asyncJob.status === asyncJobStatus.COMPLETE ? asyncJobDataType.SUCCESS : asyncJobDataType.DANGER;
        asyncJobData.messageData = asyncJob.status === asyncJobStatus.COMPLETE ?
          { translationString: this.getTenantAsyncJobAlertString(asyncJobStatus.COMPLETE, asyncJob.jobType, asyncJob.clientJobTag) }: //COMPLETE
          { translationString: this.getTenantAsyncJobAlertString(asyncJobStatus.FAILED, asyncJob.jobType, asyncJob.clientJobTag) }; //FAILED
        asyncJobData.message = this.translateService.instant(asyncJobData.messageData.translationString);           
        // For now, only need to show link for the failed report generation notifications
        if (asyncJob.jobType == BillingAsyncJobType.REPORT_REGENERATION && asyncJob.status === asyncJobStatus.FAILED) {
          asyncJobData.link = `/accounts/${this.getAccountId()}/zappa-support?tab=report&jobId=${asyncJob.jobId}`;
          asyncJobData.linkMessage = this.translateService.instant('async_jobs.zappa_phone_report.details');
        }
        update = true;

        if (asyncJob.status === asyncJobStatus.COMPLETE) {
          console.log(`[${logContext}.updateTenantAsyncJobData] ${asyncJob.jobType} success`, asyncJob);
          this.notifierService.notify('success', this.translateService.instant( this.getTenantAsyncJobToastString(asyncJobStatus.COMPLETE, asyncJob.jobType, asyncJob.clientJobTag)));
        } else {
          console.error(`[${logContext}.updateTenantAsyncJobData]  ${asyncJob.jobType} fail`, asyncJob);
          this.notifierService.notify('error', this.translateService.instant(this.getTenantAsyncJobToastString(asyncJobStatus.FAILED, asyncJob.jobType, asyncJob.clientJobTag)));
        }
      }
    }
    return update;
  }

  private getTenantAsyncJobAlertString(status: asyncJobStatus, jobType: BillingAsyncJobType, clientJobTag: any): string {
    let alertString = "async_jobs";
    let typeString = this.determineTenantResource(jobType, clientJobTag);
    let statusString = "";
    switch (status) {
      case asyncJobStatus.COMPLETE:
        statusString = "successful_alert";
        break;
      case asyncJobStatus.FAILED:
        statusString = "failed_alert";
        break;
      default:
        statusString = "in_progress_alert";
        break;
    };
    return alertString + "."+typeString+"."+statusString; //e.g "async_jobs.zappa_integ_delete.successful_alert"
  }

  private getTenantAsyncJobToastString(status: asyncJobStatus, jobType: BillingAsyncJobType, clientJobTag: any): string {
    let toastString = "async_jobs";
    let typeString = this.determineTenantResource(jobType, clientJobTag);
    let statusString = "";
    switch (status) {
      case asyncJobStatus.COMPLETE:
        statusString = "successful_toast";
        break;
      case asyncJobStatus.FAILED:
        statusString = "failed_toast";
        break;
      default:
        statusString = "in_progress_toast";
        break;
    };
    return toastString + "."+typeString+"."+statusString; //e.g "async_jobs.zappa_integ_delete.successful_toast"
  }

  determineTenantResource(jobType: BillingAsyncJobType, clientJobTag?: any) {
    let resource = '';
    switch (jobType) {
      case BillingAsyncJobType.REPORT_REGENERATION:
        resource = 'zappa_phone_report';
        break;
      case BillingAsyncJobType.TENANT_DELETION:        
        // backward compatibility when there is no clientJobTag it's a Zoom deletion
        resource = clientJobTag?.tenantType == TenantType.UNIFY_PHONE ? 'unify_phone_integ_delete' : 'zappa_integ_delete';
        break;
      default:
        break;
    };
    return resource;
  }

  handleTenantAsyncJobChangeEvent(updateJob) {

    let currentJobData = this.asyncJobs?.find(aj => aj.asyncJobId === updateJob.jobId && aj.owner === updateJob.owner);
    console.log("async job, found job", currentJobData)
    if (currentJobData)
    {
      // update
      this.updateTenantAsyncJobData(currentJobData, updateJob)
      this.asyncJobs = this.asyncJobs.concat([]);
    }
    else
    {
      // new job
      let asyncJobData = this.createNewTenantAsyncJobData(updateJob);
      this.asyncJobs = this.asyncJobs.concat([asyncJobData]);
    }
  }

  private parseIfDefined(string: string | undefined): any[] {
    return string? JSON.parse(string) : undefined;
  }

  private getAsyncJobDataFromResponse(workerResponse: WorkerCollection.Worker, asyncJob: UserTemplateAsyncJobData, notify: boolean = false) {   
    let notification: {
      type: string,
      message: string,
    };

    /**
     * The result string will have the following format:
     * "Successfully imported x User Templates from the solution." where x is any number including 0.
    */
    const result: string = workerResponse?.outputs?.find(output => output.id == "result" && output.value !== null)?.value  || undefined;
    const successDetails: any[] = this.parseIfDefined(workerResponse?.outputs?.find(output => output.id == "successDetails" && output.value !== null)?.value || undefined);
    const failureDetails: any[] = this.parseIfDefined(workerResponse?.outputs?.find(output => output.id == "failureDetails" && output.value !== null)?.value || undefined);
    const accountId = this.getAccountId();

    // Verify the result string indicates success
    if (result) {
      // Lets define some constants with parts of the results string so we can use them to parse/get the data we want.
      const SUCCESS_MSG_1 = "Successfully imported ";
      const SUCCESS_MSG_2 = " User Templates from the solution.";

      // Determine if failure details includes missing role message
      let numTemplatesMissingRole = failureDetails?.filter(detail => detail.description?.includes('role'))?.length || 0;
      // Find number of successful imports from result string
      let indexStart = SUCCESS_MSG_1.length; // Start after the first half of the success message.
      let indexEnd = result.indexOf(SUCCESS_MSG_2); // End at start of second half of the success message.
      let numTemplatesImported = +result.substring(indexStart, indexEnd);
      if (numTemplatesImported > 0) {
        // Success
        asyncJob.status = asyncJobStatus.COMPLETE;
        asyncJob.type = asyncJobDataType.SUCCESS;
        asyncJob.dismissible = true;
        
        if (asyncJob.templateName) {
          let userTemplateId: string = successDetails?.find(detail => detail.userTemplateName == asyncJob.templateName)?.userTemplateId;
          asyncJob.link = userTemplateId ? `/accounts/${accountId}/user-templates/templates/${userTemplateId}` : `/accounts/${accountId}/user-templates`;
          asyncJob.linkMessage = asyncJob.templateName;
          asyncJob.messageData = {
            translationString: 'async_jobs.user_templates.byNameSuccess',
            translationStringData: { templateName: asyncJob.templateName },
          };
          notification = {
            type: 'success',
            message: this.translateService.instant('user-templates.import.byNameSuccess', { templateName: asyncJob.templateName }),
          }
        } else {
          asyncJob.link = `/accounts/${accountId}/user-templates/${asyncJob.workerId}`;
          asyncJob.linkMessage = this.translateService.instant('menus.UserTemplates');
          asyncJob.messageData = {
            translationString: 'async_jobs.user_templates.success',
            translationStringData: { numTemplatesImported: numTemplatesImported },
          };
          notification = {
            type: 'success',
            message: this.translateService.instant('user-templates.import.success', { numTemplatesImported: numTemplatesImported }),
          };
          if (numTemplatesMissingRole > 0) {
            let missingRoleWarning = numTemplatesMissingRole > 1 ? 'user-templates.import.missingRoleWarningPlural' : 'user-templates.import.missingRoleWarning';
            notification = {
              type: 'warning',
              message: this.translateService.instant(missingRoleWarning, {numTemplatesMissingRole: numTemplatesMissingRole}),
            };
          }
        }
      } else {
        // Failure - there is no result string
        asyncJob.status = asyncJobStatus.FAILED;
        asyncJob.type = asyncJobDataType.DANGER;
        asyncJob.dismissible = true;
        asyncJob.link = `/accounts/${accountId}/user-templates/${asyncJob.workerId}`;
        asyncJob.linkMessage = asyncJob.templateName ? asyncJob.templateName : this.translateService.instant('menus.UserTemplates');

        if (numTemplatesMissingRole > 0) {
          asyncJob.messageData = asyncJob.templateName ?
          {
            translationString: 'async_jobs.user_templates.byNameMissingRoleError'
          }:
          {
            translationString: 'async_jobs.user_templates.missingRoleError',
            translationStringData: { numTemplatesMissingRole: numTemplatesMissingRole },
          };
          notification = {
            type: 'error',
            message: numTemplatesMissingRole > 1 ? 
              this.translateService.instant('user-templates.import.missingRoleWarningPlural', {numTemplatesMissingRole: numTemplatesMissingRole}) : 
              asyncJob.templateName ? this.translateService.instant('user-templates.import.byNameMissingRoleError', { templateName: asyncJob.templateName }) :
              this.translateService.instant('user-templates.import.missingRoleWarning', {numTemplatesMissingRole: numTemplatesMissingRole}),
          };
        } else {
          asyncJob.messageData = { translationString: asyncJob.templateName ? 'async_jobs.user_templates.byNameError' : 'async_jobs.user_templates.error' };
          notification = {
            type: 'error',
            message: asyncJob.templateName ? 
              this.translateService.instant('user-templates.import.byNameError', { templateName: asyncJob.templateName }) :
              this.translateService.instant('user-templates.import.error')
          };
        }
      }
    } else {
      asyncJob.status = asyncJobStatus.FAILED;
      asyncJob.type = asyncJobDataType.DANGER;
      asyncJob.dismissible = true;
      asyncJob.link = `/accounts/${accountId}/user-templates/${asyncJob.workerId}`;
      asyncJob.linkMessage = asyncJob.templateName ? asyncJob.templateName : this.translateService.instant('menus.UserTemplates');
      asyncJob.messageData = asyncJob.templateName ?
      {
        translationString: 'async_jobs.user_templates.byNameError',
        translationStringData: { templateName: asyncJob.templateName },
      }:
      {
        translationString: 'async_jobs.user_templates.error',
      };
      notification = {
        type: 'error',
        message: asyncJob.templateName ? 
          this.translateService.instant('user-templates.import.byNameError', { templateName: asyncJob.templateName }) :
          this.translateService.instant('user-templates.import.error'),
      };
    }

    asyncJob.message = this.translateService.instant(asyncJob.messageData.translationString, asyncJob.messageData.translationStringData);
    this.asyncJobsChanged.next([...this._asyncJobs]);
    if (notify && notification) {
      this.notifierService.notify(notification.type, notification.message);
    }
  }

  async addMiVBAsyncJobAndPollById(jobId: string, userId: string, httpMethod: asyncJobHttpMethod, jobLink: string, maxTime: number){
    let asyncJobData = {
      asyncJobId: jobId,
      userId: userId,
      owner: asyncJobOwner.MIVB,
      dismissible: false,
      type: asyncJobStatus.IN_PROGRESS,
    } as MiVBAsyncJobData;

    try {
      asyncJobData.userName = await this.getUserName(asyncJobData.userId);
      asyncJobData.link = jobLink;
      asyncJobData.message = this.translateService.instant(this.getAsyncJobAlertString(asyncJobStatus.IN_PROGRESS, httpMethod), { user: asyncJobData.userName ?? asyncJobData.userId });
      this.addAsyncJob(asyncJobData, httpMethod);
      let result = await this.pollAsyncJobStatusById(jobId, maxTime);
      return Promise.resolve(result);
    } catch (error) {
      //failure could occur in either getUserName or pollAsyncJobStatusById
      return Promise.reject(error);
    }
  }

  addAsyncJob(asyncJob: MiVBAsyncJobData | UserTemplateAsyncJobData | MiccbAsyncJobData, httpMethod?: asyncJobHttpMethod): void {
    let asyncJobData = this.asyncJobs?.find(aj => aj.asyncJobId === asyncJob.asyncJobId);
    //don't re-add the job in case it was already added
    if(!asyncJobData){
      if (asyncJob.type == asyncJobDataType.IN_PROGRESS) {
        if(asyncJob.owner === asyncJobOwner.MIVB || asyncJob.owner === asyncJobOwner.MICCB){
          this.notifierService.notify('info', this.translateService.instant(this.getAsyncJobToastString(asyncJobStatus.IN_PROGRESS, httpMethod), { user: asyncJob.userName }));
        } 
        else if (asyncJob.owner === asyncJobOwner.USER_TEMPLATES) {
          asyncJob.messageData = asyncJob.templateName ?
          {
            translationString: 'async_jobs.user_templates.byNameInProgress',
            translationStringData: { templateName: asyncJob.templateName },
          }:
          {
            translationString: 'async_jobs.user_templates.inProgress',
          };
          asyncJob.message = this.translateService.instant(asyncJob.messageData.translationString, asyncJob.messageData.translationStringData);
          this.notifierService.notify('info', asyncJob.templateName ? 
            this.translateService.instant('user-templates.import.byNameInProgress', { templateName: asyncJob.templateName }) :
            this.translateService.instant('user-templates.import.inProgress'));
        }
      }
      if(!asyncJob.details){
        asyncJob.details = this.getLocaleDateString(new Date(), this.translateService.currentLang); 
      }
      let newAsyncJobs = [asyncJob];
      this.asyncJobs = this.asyncJobs.concat(newAsyncJobs);
    }
    this.initAsyncJobs(asyncJob.owner as asyncJobOwner);
  }

  /*
    - will poll a particular job and check its status every 2 seconds.
    - it will stop polling when the job status has changed to COMPLETE or FAILED, 
      or we reached the maxTime
    -  Always return the job because some methods may want to look at the statusCode of the failure.
    - maxTime is the maximum amount of time to wait for job to either complete or fail
  */
  pollAsyncJobStatusById(asyncJobId: string, maxTime?: number): Promise<PolledJobResult>{
    return new Promise( (resolve,reject) => {
      let start = new Date().getTime();
      const intervalId = setInterval( () => {
        maxTime = maxTime || 180000; //default of 3 minutes
        let job = this.getAsyncJobById(asyncJobId);
        let jobStatus = job.status;
        if(jobStatus === asyncJobStatus.COMPLETE){
          clearInterval(intervalId);
          let returnData: PolledJobResult = {
            pollResult: "async job succeeded",
            job: job
          };
          resolve(returnData);
        }
        else if(jobStatus === asyncJobStatus.FAILED){
          clearInterval(intervalId);
          let returnData: PolledJobResult = {
            pollResult: "async job failed",
            job: job
          };
          reject(returnData);
        }
        else{
          let timer = (new Date().getTime() - start);
          if( timer > maxTime){
            clearInterval(intervalId);
            let returnData: PolledJobResult = {
              pollResult: "async job took too long",
              job: job
            };
            reject(returnData);
          }
        }
      },2000);
    });
  }

  getAsyncJobById(asyncJobId: string): AsyncJobData {
    return this.asyncJobs?.find(asyncJob => asyncJob.asyncJobId === asyncJobId);
  }

  getAsyncJobStatusById(asyncJobId: string): string {
    return this.asyncJobs?.find(asyncJob => asyncJob.asyncJobId === asyncJobId)?.status;
  }

  wasAsyncJobCompletedSuccessfully(asyncJobId: string): boolean {
    return !!(this.asyncJobs?.find(asyncJob => asyncJob.asyncJobId === asyncJobId)?.status === asyncJobStatus.COMPLETE);
  }

  getUserJobIdInProgress(userId: string): string {
    return this.asyncJobs?.find((asyncJob: MiVBAsyncJobData) => asyncJob.userId === userId && (asyncJob.status !== asyncJobStatus.COMPLETE && asyncJob.status !== asyncJobStatus.FAILED))?.asyncJobId ?? '';
  }

  getAllJobIdsofUserWithCertainStatus(userId: string, status?: asyncJobStatus): string[]{
    let usersJobsWithStatus = [];
    if(userId){
      switch (status) {
        case asyncJobStatus.IN_PROGRESS: //keeping same logic as getUserJobIdInProgress above - INPROGRESS and QUEUED
          usersJobsWithStatus = this.asyncJobs?.filter((asyncJob: MiVBAsyncJobData) => asyncJob.userId === userId && (asyncJob.status !== asyncJobStatus.COMPLETE && asyncJob.status !== asyncJobStatus.FAILED))?.map(job => job.asyncJobId);
          break;
        case asyncJobStatus.COMPLETE:
          usersJobsWithStatus = this.asyncJobs?.filter((asyncJob: MiVBAsyncJobData) => asyncJob.userId === userId && asyncJob.status === asyncJobStatus.COMPLETE)?.map(job => job.asyncJobId);
          break;
        case asyncJobStatus.FAILED:
          usersJobsWithStatus = this.asyncJobs?.filter((asyncJob: MiVBAsyncJobData) => asyncJob.userId === userId && asyncJob.status === asyncJobStatus.FAILED)?.map(job => job.asyncJobId);
          break;
        default: //no status given -> return all jobs for this userId
        usersJobsWithStatus = this.asyncJobs?.filter((asyncJob: MiVBAsyncJobData) => asyncJob.userId === userId)?.map(job => job.asyncJobId);  
        break;
      }
    }
    return usersJobsWithStatus
  }

  async updateAlert(alertUpdate: { alert: AlertData, update: string }) {
    if (alertUpdate && alertUpdate.alert && alertUpdate.update) {
      if(alertUpdate.alert.id){
        let asyncJobData = this.asyncJobs?.find(aj => aj.asyncJobId === alertUpdate.alert.id) as any;
        if (asyncJobData) {
          if(alertUpdate.update === alertUpdateTypes.CLOSE){
            if(asyncJobData.owner === asyncJobOwner.MIVB){
              let accountId = this.getAccountId();
  
              try {
                await this.miVBAccountSettingsService.putAsyncJobById(alertUpdate.alert.id, { 'acknowledged': true });
                this.removeAsyncJob(asyncJobData.asyncJobId);
              } catch (error) {
                console.log(`Unable to update async job for account: ${accountId} -> ${error}`);
              }
            } else if(asyncJobData.owner === asyncJobOwner.USER_TEMPLATES){
              try {
                await this.workflowService.terminateWorkerByWorkerAndActivityId({activityId: this.importUserTemplatesActivityId, workerId: asyncJobData.workerId});
                this.removeAsyncJob(asyncJobData.asyncJobId);
              } catch (error) {
                console.log(`Unable to terminate worker: ${asyncJobData.workerId} -> ${error}`);
              }
            }
            else if(asyncJobData.owner === asyncJobOwner.ZAPPA_PHONE || asyncJobData.owner === asyncJobOwner.UNIFY_PHONE){
              let accountId = this.getAccountId();
  
              try {
                await this.tenantsService.updateAsyncJob(alertUpdate.alert.id, { 'acknowledged': true });
                this.removeAsyncJob(asyncJobData.asyncJobId);
              } catch (error) {
                console.log(`Unable to update tenant async job for account: ${accountId} -> ${error}`);
              }
            }
          }
          if(alertUpdate.update === alertUpdateTypes.LINK && alertUpdate.alert?.link){
            if(asyncJobData.owner === asyncJobOwner.USER_TEMPLATES && this.router.url == alertUpdate.alert.link){
              // User is on the user-templates page and has clicked a notification link which navigates to the user-templates page
              // ok, we will reload the app so that the new imported template information is loaded
              const routeReuseStrategy = this.router.routeReuseStrategy.shouldReuseRoute;
              const onSameUrlNavigation = this.router.onSameUrlNavigation;

              try {
                this.router.routeReuseStrategy.shouldReuseRoute = () => false;
                this.router.onSameUrlNavigation = 'reload';
                await this.router.navigate([alertUpdate.alert.link]);
              }
              finally {
                this.router.routeReuseStrategy.shouldReuseRoute = routeReuseStrategy;
                this.router.onSameUrlNavigation = onSameUrlNavigation;
              }
            } else if(asyncJobData.owner === asyncJobOwner.MIVB || asyncJobData.owner === asyncJobOwner.USER_TEMPLATES || asyncJobData.owner === asyncJobOwner.ZAPPA_PHONE) {
              this.router.navigateByUrl(alertUpdate.alert.link);
            }
          }
        }
      }
    }
  }

  isUserUpdateInProgress(userId: string, asyncJobId: string): boolean {
    return !!this.asyncJobs?.find((asyncJob: MiVBAsyncJobData) => asyncJob.userId === userId && asyncJob.asyncJobId === asyncJobId && 
      (asyncJob.status !== asyncJobStatus.COMPLETE && asyncJob.status !== asyncJobStatus.FAILED));
  }

  getUserName(userId: string): Promise<string> {
    if (!this.userNameCache.has(userId)) {
      this.userNameCache.set(userId, this._getUserName(userId))
    }
    return this.userNameCache.get(userId);
  }
  
  private async _getUserName(userId: string): Promise<string> {
    let userName = '';

    let userAsyncJob = this.asyncJobs.find((aj: MiVBAsyncJobData) => aj.userId === userId) as MiVBAsyncJobData;
    if (userAsyncJob) {
      userName = userAsyncJob.userName;
    }

    if (!userName) {
      try {
        let user = await this.adminService.getUserWithoutFrills(userId);
        if (user) {
          userName = user.displayName ?? user.firstName ?? user.lastName;
        }
      } catch (error) {
        console.log(`Unable to fetch user in asyncJobsService - userId: ${userId}`);
        this.userNameCache.delete(userId);
        throw error;
      }
    }
    return userName;
  }

  notifyLanguageChange(lang: string): void {
    if(this.asyncJobs && this.asyncJobs.length){
      let asyncJobsTranslated = [];
      this.asyncJobs.forEach(asyncJob => {
        let asyncJobTranslated = { ... asyncJob };
        asyncJobTranslated.message = this.translateService.instant(asyncJob.messageData.translationString, asyncJob.messageData.translationStringData);
        asyncJobTranslated.details = this.getLocaleDateString(asyncJob.createdOn, lang); 
        asyncJobTranslated.btnCloseText = this.translateService.instant('async_jobs.btn_close_msg'),
        asyncJobsTranslated.push(asyncJobTranslated);
      });
      this.asyncJobs = asyncJobsTranslated;
    }
  }

  getLocaleDateString(dateTime: Date, lang: string): string {
    let localeDateTime = '';
    try{
      if(dateTime && lang){
        localeDateTime = new Date(Date.parse(dateTime + '')).toLocaleDateString(lang, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }); 
      }
    }
    catch (error) {
      console.log(`Unable to convert dateTime - dateTime: ${dateTime} lang: ${lang}`);
    }
    return localeDateTime;
  }
}
